import type { AppEntry } from '@/types/app';

interface Props {
  apps: AppEntry[];
  recentIds: string[];
  onOpen: (app: AppEntry) => void;
}

/**
 * F5: при пустом recent — рендерим empty-placeholder с подсказкой про Cmd+K,
 * а не скрываем секцию полностью (пользователь должен видеть, что
 * «Недавнее» — это вообще существует).
 *
 * Reconciliation: пересечение recentIds × apps. id, которых уже нет в каталоге
 * (например, удалили из apps.yaml), просто игнорим — без ошибок.
 */
export default function RecentStrip({ apps, recentIds, onOpen }: Props) {
  const byId = new Map(apps.map((a) => [a.id, a]));
  const items = recentIds
    .map((id) => byId.get(id))
    .filter((x): x is AppEntry => Boolean(x) && x!.status !== 'soon');

  if (items.length === 0) {
    return (
      <div className="recent-empty">
        Здесь появятся сервисы, которые вы открывали.
      </div>
    );
  }

  return (
    <div className="recent-strip" role="list" aria-label="Недавно открытые">
      {items.map((app) => (
        <button
          key={app.id}
          type="button"
          role="listitem"
          className="recent-chip"
          data-status={app.status}
          onClick={() => onOpen(app)}
          title={app.status === 'beta' ? `${app.name} (Beta)` : app.name}
        >
          <span className="recent-chip__icon" aria-hidden="true">{app.icon}</span>
          <span>{app.name}</span>
          {/* F20: beta-индикатор внутри chip — пользователь видит,
              что сервис в нестабильной фазе, даже без визита на карточку.
              `live` намеренно без точки: chip уже значит «недавно открытое = живое». */}
          {app.status === 'beta' && (
            <span className="recent-chip__beta-dot" aria-label="beta" />
          )}
        </button>
      ))}
    </div>
  );
}
